import { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import PlaidConnect from '../components/PlaidConnect';
import MoovPayouts from '../components/MoovPayouts';
import { TrendingUp, AlertTriangle, DollarSign, Lock, FileText, Activity } from 'lucide-react';
import type { Invoice, Finding, Recovery, HealthCheck } from '../../shared/types';
import { apiFetch } from '../lib/api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    verifiedSavings: 0,
    activeRecoveries: 0,
    vendorsAtRisk: 0,
    activeHolds: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [recentFindings, setRecentFindings] = useState<Finding[]>([]);
  const [health, setHealth] = useState<HealthCheck | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoices, findings, recoveries, healthData] = await Promise.all([
          apiFetch<Invoice[]>('/api/invoices'),
          apiFetch<Finding[]>('/api/findings'),
          apiFetch<Recovery[]>('/api/recoveries'),
          apiFetch<HealthCheck>('/api/health'),
        ]);

        setRecentInvoices(invoices.slice(0, 5));
        setRecentFindings(findings.slice(0, 5));
        setHealth(healthData);

        const totalSavings = findings.reduce((sum: number, f: Finding) => sum + (f.amount || 0), 0);
        const activeRecoveryCount = recoveries.filter(
          (r: Recovery) => r.approval_status === 'PENDING'
        ).length;
        const vendorSet = new Set(findings.map((f: Finding) => f.invoice_id));

        setStats({
          verifiedSavings: totalSavings,
          activeRecoveries: activeRecoveryCount,
          vendorsAtRisk: vendorSet.size,
          activeHolds: activeRecoveryCount,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };

    fetchData();
  }, []);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-xs text-emerald-400 font-medium">+12.5%</span>
            </div>
            <p className="text-sm text-slate-400 mb-1">Verified Savings</p>
            <p className="text-3xl font-bold text-white">${stats.verifiedSavings.toFixed(2)}</p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
              </div>
              <span className="text-xs text-cyan-400 font-medium">Active</span>
            </div>
            <p className="text-sm text-slate-400 mb-1">Active Recoveries</p>
            <p className="text-3xl font-bold text-white">{stats.activeRecoveries}</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
              <span className="text-xs text-orange-400 font-medium">Review</span>
            </div>
            <p className="text-sm text-slate-400 mb-1">Vendors at Risk</p>
            <p className="text-3xl font-bold text-white">{stats.vendorsAtRisk}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-xs text-purple-400 font-medium">Protected</span>
            </div>
            <p className="text-sm text-slate-400 mb-1">Active Holds</p>
            <p className="text-3xl font-bold text-white">{stats.activeHolds}</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Invoices */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Recent Invoice Scans</h2>
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              {recentInvoices.length > 0 ? (
                <div className="space-y-3">
                  {recentInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{invoice.vendor_name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{invoice.invoice_date || 'No date'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-white">
                          ${invoice.amount?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-xs text-slate-500">{invoice.category || 'Other'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No invoices scanned yet</p>
              )}
            </div>

            {/* Recent Findings */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Recent Findings</h2>
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
              {recentFindings.length > 0 ? (
                <div className="space-y-3">
                  {recentFindings.map((finding) => (
                    <div
                      key={finding.id}
                      className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{finding.finding_type}</p>
                        <p className="text-xs text-slate-500">{finding.description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-orange-400">
                          ${finding.amount?.toFixed(2) || '0.00'}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          finding.severity === 'HIGH' ? 'bg-red-500/20 text-red-400' :
                          finding.severity === 'MEDIUM' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {finding.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-8">No findings detected yet</p>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Integrations */}
            <PlaidConnect />
            <MoovPayouts />

            {/* System Health */}
            {health && (
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">System Health</h2>
                  <Activity className="w-5 h-5 text-slate-400" />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Overall Score</span>
                    <span className={`text-lg font-bold ${
                      health.health_score >= 80 ? 'text-emerald-400' :
                      health.health_score >= 50 ? 'text-orange-400' :
                      'text-red-400'
                    }`}>
                      {health.health_score}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Plaid</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      health.plaid_status === 'connected' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {health.plaid_status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Moov</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      health.moov_status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {health.moov_status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">OCR Engine</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400">
                      {health.ocr_status}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
